import { useState, useEffect, memo } from 'react';
import { Tabs, Box } from '@mantine/core';
import { IconList, IconDeviceGamepad } from '@tabler/icons-react';
import { useBotGame, useBotSettings, useBotActions } from './BotContext';
import { COLORS } from '../config/constants';

// Internal components
import DashboardHeader from './livedashboard/DashboardHeader';
import ControlPanel from './livedashboard/ControlPanel';
import TimelinePanel from './livedashboard/TimelinePanel';
import PodiumDisplay from './livedashboard/PodiumDisplay';
import ActionFooter from './livedashboard/ActionFooter';

/**
 * BotLiveDashboard component provides a real-time interface to monitor and control bots during a game.
 * It includes status indicators, manual/automatic answer controls, and a live event timeline.
 * @param {Object} props - Component properties.
 * @param {Array} [props.demoHistory] - Optional history override for demo mode.
 */
const BotLiveDashboard = memo(({ demoHistory }) => {
    const {
        gameState, botCount, timeLeft, totalTime,
        answerMode, timeline, podium, questionNumber
    } = useBotGame();

    const { form } = useBotSettings();
    const { setAnswerMode, sendAnswer, disconnectBots } = useBotActions();

    const [confirmTerminate, setConfirmTerminate] = useState(false);
    const [pulseColor, setPulseColor] = useState(null);
    const [history, setHistory] = useState([]);
    const colors = [COLORS.RED, COLORS.BLUE, COLORS.YELLOW, COLORS.GREEN];

    const displayHistory = demoHistory || history;

    /**
     * Handles sending an answer command and updates the local UI feedback state.
     * @param {number} index - The index of the answer option.
     */
    const handleCommand = (index) => {
        sendAnswer(index);
        setPulseColor(colors[index]);
        setTimeout(() => setPulseColor(null), 150);
        
        if (!demoHistory) {
            setHistory(prev => [index, ...prev].slice(0, 5));
        }
    };

    /**
     * Set up keyboard listeners for manual answering (keys 1-4).
     */
    useEffect(() => {
        const handleKeys = (e) => {
            if (gameState === 'question' && answerMode === 'own') {
                if (e.key >= 1 && e.key <= 4) handleCommand(Number(e.key) - 1);
            }
        };
        window.addEventListener('keydown', handleKeys);
        return () => window.removeEventListener('keydown', handleKeys);
    }, [gameState, answerMode]);

    /**
     * Timer for resetting the termination confirmation state.
     */
    useEffect(() => {
        if (confirmTerminate) {
            const timer = setTimeout(() => setConfirmTerminate(false), 3000);
            return () => clearTimeout(timer);
        }
    }, [confirmTerminate]);

    return (
        <Box style={{ border: pulseColor ? `2px solid ${pulseColor}` : '2px solid transparent', transition: 'border 0.1s ease', borderRadius: '8px' }}>
            <DashboardHeader 
                gameState={gameState}
                questionNumber={questionNumber}
                botCount={botCount}
                form={form}
                timeLeft={timeLeft}
                totalTime={totalTime}
            />

            <Tabs defaultValue="control" variant="pills" radius="md" p="md">
                <Tabs.List grow mb="md">
                    <Tabs.Tab value="control" leftSection={<IconDeviceGamepad size={16} />}>Control</Tabs.Tab>
                    <Tabs.Tab value="feed" leftSection={<IconList size={16} />}>Timeline</Tabs.Tab>
                </Tabs.List>

                <Tabs.Panel value="control">
                    {gameState === 'question' ? (
                        <ControlPanel 
                            answerMode={answerMode}
                            setAnswerMode={setAnswerMode}
                            handleCommand={handleCommand}
                            displayHistory={displayHistory}
                            colors={colors}
                        />
                    ) : (
                        <PodiumDisplay gameState={gameState} podium={podium} />
                    )}
                </Tabs.Panel>

                <Tabs.Panel value="feed">
                    <TimelinePanel timeline={timeline} colors={colors} />
                </Tabs.Panel>
            </Tabs>

            <ActionFooter 
                confirmTerminate={confirmTerminate}
                onTerminate={() => confirmTerminate ? disconnectBots() : setConfirmTerminate(true)}
            />
        </Box>
    );
});

export default BotLiveDashboard;